/* eslint-disable function-paren-newline */
import React, { useState, useEffect } from 'react';
import { CloseIcon } from '@resortpass/solar-icons';
import Chip from '@solar/common/Chip/Chip';
import { CheckboxCustomV2 as CheckboxCustom } from '@components/common/CheckboxCustom/CheckboxCustom';
import { CollapsibleContainer2 as CollapsibleContainer } from '@components/common/CollapsibleContainer/CollapsibleContainer';
import { SearchState } from '@customTypes/search';
import useIsMobile from '@hooks/useIsMobile';
import cn from '@helpers/className';
import {
  AmenitiesFriendlyNames,
  HotelStarsFriendlyNames,
  GuestRatingFriendlyNames,
  VibesFriendlyNames,
  PoolFeatures,
  PoolProductTypes,
  SpaFeatures,
  SpaProductTypes,
  ProductCategorySpecificFilters,
  useSearchContext,
} from '@context/SearchContext2025';

type Props = {
  onChange?: (s: SearchState) => void;
};

export default function SRPFilters({ onChange = () => {} }: Props) {
  const searchContext = useSearchContext();
  const isMobile = useIsMobile({ isSolarSystem: true });
  const {
    searchParams: { filters, product_category: productCategory },
  } = searchContext;
  const [fireOnChange, setFireOnChange] = useState(false);

  // -------------------------------------------------------------------
  // Flags for Showing / Hiding Specific Filters -----------------------
  // -------------------------------------------------------------------
  const shouldShowVibes = productCategory === '' || productCategory === 'pool';
  const shouldShowPoolFeatures = ['', 'pool', 'day room'].includes(productCategory);
  const shouldShowSpaFeatures = ['spa'].includes(productCategory);
  const hiddenProductTypes = (() => {
    if (productCategory === 'day room') {
      return ['Cabana', 'Daybed'];
    }
    return [];
  })();

  const hiddenAmenities = ((): Array<keyof SearchState['filters']['amenities']> => {
    if (productCategory === 'day room') {
      return ['showers', 'lockers'];
    }

    if (productCategory === 'spa') {
      return ['beach', 'all-inclusive', 'wheelchair', 'showers', 'lockers'];
    }
    return [];
  })();

  useEffect(() => {
    if (fireOnChange) {
      onChange(searchContext.searchParams);
      setFireOnChange(false);
    }
  }, [fireOnChange, onChange, searchContext.searchParams]);

  const handleOnlyAvailable = (onlyAvailable: boolean) => {
    searchContext.setOnlyAvailable(onlyAvailable);
    setFireOnChange(true);
  };

  const getHotelStarsFilterSetter = (star: keyof SearchState['filters']['hotel_stars']) => () => {
    searchContext.setHotelStars({
      // set selected hotel class rating filter
      ...{ [star]: !filters.hotel_stars[star] },
    });
    setFireOnChange(true);
  };

  const getAmenityFilterSetter = (amenity: keyof SearchState['filters']['amenities']) => () => {
    searchContext.setAmenities({
      [amenity]: !filters.amenities[amenity],
    });
    setFireOnChange(true);
  };

  const getVibeFilterSetter = (vibe: keyof SearchState['filters']['vibes']) => () => {
    searchContext.setVibes({
      [vibe]: !filters.vibes[vibe],
    });
    setFireOnChange(true);
  };

  const getGuestRatingFilterSetter =
    (rating: keyof SearchState['filters']['guest_ratings']) => () => {
      searchContext.setGuestRatings({
        [rating]: !filters.guest_ratings[rating],
      });
      setFireOnChange(true);
    };

  const getProductTypeFilterSetter = (productTypeId: number) => () => {
    const containsProductType = filters.product_types.includes(productTypeId);
    if (containsProductType) {
      searchContext.removeProductType(productTypeId);
    } else {
      searchContext.addProductType(productTypeId);
    }
    setFireOnChange(true);
  };

  type Filter<T> = {
    label: string;
    value: T;
  };

  const AmenityFilters: Filter<keyof SearchState['filters']['amenities']>[] = Object.entries(
    AmenitiesFriendlyNames,
  )
    .filter(
      ([key]) =>
        !hiddenAmenities.includes(key as keyof SearchState['filters']['amenities']) &&
        !ProductCategorySpecificFilters.has(key as keyof SearchState['filters']['amenities']),
    )
    .map(([value, label]) => ({
      label,
      value: value as keyof SearchState['filters']['amenities'],
    }));

  const PoolFilters: Filter<keyof SearchState['filters']['amenities']>[] = PoolFeatures.map(
    (item) => ({
      value: item,
      label: AmenitiesFriendlyNames[item],
    }),
  );

  const SpaFilters: Filter<keyof SearchState['filters']['amenities']>[] = SpaFeatures.map(
    (item) => ({
      value: item,
      label: AmenitiesFriendlyNames[item],
    }),
  );

  const GuestRatingFilters: Filter<keyof SearchState['filters']['guest_ratings']>[] =
    Object.entries(GuestRatingFriendlyNames).map(([value, label]) => ({
      label,
      value: value as keyof SearchState['filters']['guest_ratings'],
    }));
  const HotelStarFilters: Filter<keyof SearchState['filters']['hotel_stars']>[] = Object.entries(
    HotelStarsFriendlyNames,
  ).map(([value, label]) => ({
    label,
    value: value as keyof SearchState['filters']['hotel_stars'],
  }));

  const VibeFilters: Filter<keyof SearchState['filters']['vibes']>[] = Object.entries(
    VibesFriendlyNames,
  ).map(([value, label]) => ({ label, value: value as keyof SearchState['filters']['vibes'] }));
  // Applied filters
  // This is a list of filters that are currently applied to the search results.
  // Used to present the 'chips' that show the user what filters are currently applied
  // in the mobile view
  const appliedSRPFilters: Array<{ label: string; remove: () => void }> = [
    ...(filters.only_available
      ? [{ label: 'Available', remove: () => handleOnlyAvailable(false) }]
      : []),
    ...HotelStarFilters.filter((item) => filters.hotel_stars[item.value]).map((item) => ({
      label: item.label,
      remove: getHotelStarsFilterSetter(item.value),
    })),
    ...AmenityFilters.filter((item) => filters.amenities[item.value]).map((item) => ({
      label: item.label,
      remove: getAmenityFilterSetter(item.value),
    })),
    ...VibeFilters.filter((item) => filters.vibes[item.value]).map((item) => ({
      label: item.label,
      remove: getVibeFilterSetter(item.value),
    })),
    ...GuestRatingFilters.filter((item) => filters.guest_ratings[item.value]).map((item) => ({
      label: item.label,
      remove: getGuestRatingFilterSetter(item.value),
    })),
    ...PoolFilters.filter((item) => filters.amenities[item.value]).map((item) => ({
      label: item.label,
      remove: getAmenityFilterSetter(item.value),
    })),
    ...SpaFilters.filter((item) => filters.amenities[item.value]).map((item) => ({
      label: item.label,
      remove: getAmenityFilterSetter(item.value),
    })),
    ...Object.entries(PoolProductTypes)
      .filter(([, value]) => filters.product_types.includes(value))
      .map(([key, value]) => ({
        label: key,
        remove: getProductTypeFilterSetter(value),
      })),
    ...Object.entries(SpaProductTypes)
      .filter(([, value]) => filters.product_types.includes(value))
      .map(([key, value]) => ({
        label: key,
        remove: getProductTypeFilterSetter(value),
      })),
  ];

  const removeAllFilters = () => {
    searchContext.clearFilters();
    setFireOnChange(true);
  };

  return (
    <div className="w-full bottom-0 relative flex flex-col desktop:block desktop:w-auto">
      <div className="flex-1 overflow-y-auto">
        <div>
          {appliedSRPFilters.length > 0 ? (
            <div className="p-spacing-md desktop:hidden flex flex-nowrap overflow-x-auto gap-spacing-sm border-b border-rp-gray-divider no-scrollbar">
              {appliedSRPFilters.map((item, index) => (
                <Chip
                  className="cursor-pointer"
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${item.label}-${index}`}
                  text={item.label}
                  rightIcon={<CloseIcon width={16} height={16} />}
                  onClick={item.remove}
                />
              ))}
            </div>
          ) : null}

          {/* -------------------------------------------------------------
           // Hotel Class Filters -----------------------------------------
           // -------------------------------------------------------------
          */}

          <CollapsibleContainer
            className="p-spacing-md desktop:px-0"
            title={(() => {
              const count = Object.values(filters.hotel_stars).filter(Boolean).length;
              return `Hotel Class${count ? ` (${count})` : ''}`;
            })()}
          >
            <div className="flex flex-col pt-4 overflow-hidden gap-spacing-md mt-0.5 desktop:mt-0">
              {HotelStarFilters.map((item) => (
                <div className="flex items-center" key={item.value}>
                  <CheckboxCustom
                    label={item.label}
                    isChecked={filters.hotel_stars[item.value]}
                    setIsChecked={getHotelStarsFilterSetter(item.value)}
                    id={item.label}
                    isDarkBorder
                  />
                </div>
              ))}
            </div>
          </CollapsibleContainer>

          {/* -------------------------------------------------------------
           // Spa Features ------------------------------------------------
           // -------------------------------------------------------------
          */}

          <CollapsibleContainer
            className={cn({
              'spa-services-section': true,
              'p-spacing-md desktop:px-0': true,
              hidden: !shouldShowSpaFeatures,
            })}
            title={(() => {
              const count = Object.entries(SpaProductTypes).filter(([, value]) =>
                filters.product_types.includes(value),
              ).length;
              return `Spa Services${count ? ` (${count})` : ''}`;
            })()}
          >
            <div className="flex flex-col pt-4 overflow-hidden gap-spacing-md mt-0.5 desktop:mt-0">
              {Object.entries(SpaProductTypes).map(([label, value]) => (
                <div className="flex items-center" key={`${label}-product-type-filter`}>
                  <CheckboxCustom
                    label={label}
                    isChecked={filters.product_types.includes(value)}
                    setIsChecked={getProductTypeFilterSetter(value)}
                    id={`${label}-product-type-filter`}
                    isDarkBorder
                  />
                </div>
              ))}
            </div>
          </CollapsibleContainer>

          <CollapsibleContainer
            className={cn({
              'spa-features-section': true,
              'p-spacing-md desktop:px-0': true,
              hidden: !shouldShowSpaFeatures,
            })}
            title={(() => {
              const count = Object.entries(filters.amenities)
                .filter(([key]) =>
                  SpaFeatures.includes(key as keyof SearchState['filters']['amenities']),
                )
                .filter(([, value]) => value).length;
              return `Spa Features${count ? ` (${count})` : ''}`;
            })()}
          >
            <div className="flex flex-col pt-4 overflow-hidden gap-spacing-md mt-0.5 desktop:mt-0">
              {SpaFilters.map((item) => (
                <div className="flex items-center" key={item.value}>
                  <CheckboxCustom
                    label={item.label}
                    isChecked={filters.amenities[item.value]}
                    setIsChecked={getAmenityFilterSetter(item.value)}
                    id={item.label.toString()}
                    isDarkBorder
                  />
                </div>
              ))}
            </div>
          </CollapsibleContainer>
          {/* -------------------------------------------------------------
           // Pool Features -----------------------------------------------
           // -------------------------------------------------------------
          */}
          <CollapsibleContainer
            className={cn({
              'pool-features-section': true,
              'p-spacing-md desktop:px-0': true,
              hidden: !shouldShowPoolFeatures,
            })}
            title={(() => {
              const count = Object.entries(filters.amenities)
                .filter(([key]) =>
                  PoolFeatures.includes(key as keyof SearchState['filters']['amenities']),
                )
                .filter(([, value]) => value)
                .map(() => true)
                .concat(
                  Object.values(PoolProductTypes)
                    .filter((value) => filters.product_types.includes(value))
                    .map(() => true),
                ).length;
              return `Pool Features${count ? ` (${count})` : ''}`;
            })()}
          >
            <div className="flex flex-col pt-4 overflow-hidden gap-spacing-md mt-0.5 desktop:mt-0">
              {PoolFilters.slice(0, PoolFilters.length - 3).map((item) => (
                <div className="flex items-center" key={item.value}>
                  <CheckboxCustom
                    label={item.label}
                    isChecked={filters.amenities[item.value]}
                    setIsChecked={getAmenityFilterSetter(item.value)}
                    id={item.label.toString()}
                    isDarkBorder
                  />
                </div>
              ))}

              {Object.entries(PoolProductTypes)
                .filter(([key]) => !hiddenProductTypes.includes(key))
                .map(([key, value]) => (
                  <div key={`${key}-product-type-filter`} className="flex items-center">
                    <CheckboxCustom
                      label={key}
                      isChecked={filters.product_types.includes(value)}
                      setIsChecked={getProductTypeFilterSetter(value)}
                      id={`${key}-product-type-filter`}
                      isDarkBorder
                    />
                  </div>
                ))}

              {PoolFilters.slice(PoolFilters.length - 3).map((item) => (
                <div className="flex items-center" key={item.value}>
                  <CheckboxCustom
                    label={item.label}
                    isChecked={filters.amenities[item.value]}
                    setIsChecked={getAmenityFilterSetter(item.value)}
                    id={item.label.toString()}
                    isDarkBorder
                  />
                </div>
              ))}
            </div>
          </CollapsibleContainer>

          {/* -------------------------------------------------------------
           // Amenities---------------------------------------------------
           // -------------------------------------------------------------
          */}
          <CollapsibleContainer
            className="p-spacing-md desktop:px-0"
            title={(() => {
              const count = Object.entries(filters.amenities)
                .filter(
                  ([key]) =>
                    !ProductCategorySpecificFilters.has(
                      key as keyof SearchState['filters']['amenities'],
                    ),
                )
                .filter(
                  ([key]) =>
                    !hiddenAmenities.includes(key as keyof SearchState['filters']['amenities']),
                )
                .filter(([, value]) => value).length;
              return `Amenities${count ? ` (${count})` : ''}`;
            })()}
          >
            <div className="flex flex-col pt-4 overflow-hidden gap-spacing-md mt-0.5 desktop:mt-0">
              {AmenityFilters.map((item) => (
                <div className="flex items-center" key={item.value}>
                  <CheckboxCustom
                    label={item.label}
                    isChecked={filters.amenities[item.value]}
                    setIsChecked={getAmenityFilterSetter(item.value)}
                    id={item.label.toString()}
                    isDarkBorder
                  />
                </div>
              ))}
            </div>
          </CollapsibleContainer>

          {/* -------------------------------------------------------------
           // Guest Rating ------------------------------------------------
           // -------------------------------------------------------------
          */}
          <CollapsibleContainer
            className="p-spacing-md desktop:px-0"
            noBorder={!shouldShowVibes && isMobile}
            title={(() => {
              const count = Object.values(filters.guest_ratings).filter(Boolean).length;
              return `Guest Rating${count ? ` (${count})` : ''}`;
            })()}
          >
            <div className="flex flex-col pt-4 overflow-hidden gap-5 mt-0.5 desktop:mt-0">
              {GuestRatingFilters.map((item) => (
                <div className="flex items-center" key={item.value}>
                  <CheckboxCustom
                    label={item.label}
                    isChecked={filters.guest_ratings[item.value]}
                    setIsChecked={getGuestRatingFilterSetter(item.value)}
                    id={item.label}
                    isDarkBorder
                  />
                </div>
              ))}
            </div>
          </CollapsibleContainer>

          {/* -------------------------------------------------------------
           // Vibes ------------------------------------------------------
           // -------------------------------------------------------------
           */}
          <CollapsibleContainer
            noBorder={isMobile}
            className={cn({
              'vibes-section': true,
              'p-spacing-md desktop:px-0': true,
              hidden: !shouldShowVibes,
            })}
            title={(() => {
              const count = Object.values(filters.vibes).filter(Boolean).length;
              return `Vibes${count ? ` (${count})` : ''}`;
            })()}
          >
            <div className="flex flex-col pt-4 overflow-hidden gap-spacing-md mt-0.5 desktop:mt-0">
              {VibeFilters.map((item) => (
                <div className="flex items-center" key={item.value}>
                  <CheckboxCustom
                    label={item.label}
                    isChecked={filters.vibes[item.value]}
                    setIsChecked={getVibeFilterSetter(item.value)}
                    id={item.label}
                    isDarkBorder
                  />
                </div>
              ))}
            </div>
          </CollapsibleContainer>

          <button
            type="button"
            onClick={removeAllFilters}
            className="hidden text-base text-left text-solar-brand-shadow font-rp-basetica cursor-pointer desktop:block mt-16px"
          >
            Clear all
          </button>
        </div>
      </div>
    </div>
  );
}
